import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, FileDown, ChevronRight } from "lucide-react";

// Phase 25.13ao · P3 — Exit-intent modal.
//
// Fires when a visitor moves their cursor toward the top of the viewport
// (toward the close-tab / address bar). Classic exit-intent — captures
// the "I was about to leave" moment.
//
// Why we ship this: production analytics showed a heavy-machinery
// prospect spending 1h43m on the site (visiting /industries/heavy-
// machinery → /founders-guide → / → /experts → /). They left without
// converting. This modal is the safety net for sessions like that.
//
// UX rules we obey:
//   • Triggers ONCE per session (sessionStorage flag)
//   • Triggers only after the user has been on the site for 30+ seconds
//     (prevents firing on people who landed and immediately bounced)
//   • Triggers only on cursor leaving the TOP of the viewport
//     (not bottom, not sides — that's mobile-style bounce, not exit)
//   • Suppressed on mobile (no real cursor → exit-intent is unreliable)
//   • Suppressed on /app/** dashboard
//   • Suppressed on /founders-guide /pricing /signup /login (already
//     in a conversion flow)
//   • Click outside or X to close
//   • Once closed, the dismiss flag stays for 14 days

const SHOW_ONCE_FLAG = "dara_exit_intent_shown";
const DISMISS_FLAG = "dara_exit_intent_dismissed_until";
const DISMISS_DAYS = 14;
const MIN_DWELL_MS = 30_000; // 30s minimum dwell before exit-intent can fire

const HIDDEN_PATHS = new Set([
  "/founders-guide",
  "/pricing",
  "/signup",
  "/login",
  "/reset",
  "/reset-password",
]);

function isMobileWidth() {
  if (typeof window === "undefined") return true;
  return window.innerWidth < 768;
}

export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const mountedAt = useRef(Date.now());
  const hasShownThisSession = useRef(false);

  useEffect(() => {
    const path = window.location.pathname;
    if (HIDDEN_PATHS.has(path) || path.startsWith("/app/")) return;

    // Don't show in the same session twice.
    try {
      if (sessionStorage.getItem(SHOW_ONCE_FLAG) === "1") {
        hasShownThisSession.current = true;
        return;
      }
    } catch (e) {
      // sessionStorage unavailable → continue
    }

    // Don't show if dismissed in the last 14 days.
    try {
      const until = parseInt(localStorage.getItem(DISMISS_FLAG) || "0", 10);
      if (until && Date.now() < until) return;
    } catch (e) {
      // localStorage unavailable → continue
    }

    if (isMobileWidth()) return;

    const onMouseLeave = (e) => {
      // Only fire when cursor exits via the TOP of the viewport. clientY
      // <= 0 means the cursor went above the document. relatedTarget
      // being null is the strong signal it's leaving the browser entirely.
      if (e.clientY > 0) return;
      if (Date.now() - mountedAt.current < MIN_DWELL_MS) return;
      if (hasShownThisSession.current) return;
      hasShownThisSession.current = true;
      try {
        sessionStorage.setItem(SHOW_ONCE_FLAG, "1");
      } catch (err) {
        // ignore
      }
      setOpen(true);
    };

    document.addEventListener("mouseleave", onMouseLeave);
    return () => document.removeEventListener("mouseleave", onMouseLeave);
  }, []);

  const dismiss = () => {
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_FLAG, String(until));
    } catch (e) {
      // ignore
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
      data-testid="exit-intent-modal"
    >
      <div className="w-full max-w-lg border border-[#FFB000]/50 bg-black shadow-[0_30px_120px_-15px_rgba(255,176,0,0.35)]">
        {/* Header */}
        <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">
            <span className="inline-flex w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
            // BEFORE YOU GO
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="exit-intent-close"
            className="text-zinc-500 hover:text-white"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-7">
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">
            Take the founder's guide with you.
          </h2>
          <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
            One 4-page PDF. <span className="text-white font-bold">No email required.</span>{" "}
            Covers DARA's coverage of 11 jurisdictions, the typical compliance gaps
            DARA flags in the first 24 hours, and the revenue uplift our SMB cohort
            sees in the first 90 days.
          </p>

          <div className="mt-4 grid grid-cols-3 gap-2">
            {[
              { k: "11", v: "Jurisdictions" },
              { k: "24h", v: "1st brief" },
              { k: "$0", v: "To start" },
            ].map((s) => (
              <div key={s.k} className="border border-zinc-900 bg-zinc-950/50 px-2 py-2">
                <div className="font-heading font-black text-lg text-[#FFB000] tracking-tight">{s.k}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{s.v}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <Link
              to="/founders-guide"
              onClick={dismiss}
              data-testid="exit-intent-primary"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-5 py-3 hover:opacity-90"
            >
              <FileDown className="w-3.5 h-3.5" />
              Download the guide
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
            <button
              type="button"
              onClick={dismiss}
              data-testid="exit-intent-decline"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 underline-offset-4 hover:underline"
            >
              No thanks
            </button>
            <div className="ml-auto flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-zinc-600">
              <Sparkles className="w-3 h-3" />
              90s read
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
